// Constantes de domínio do Laboratório fee-based (portado do protótipo validado).
// Premissas default, chaves de classe, presets de alocação, cores e textos de
// tooltip. Cores vêm dos tokens do design-system (STATUS_COLORS + paleta de charts).

import { STATUS_COLORS } from '@modules/fee-fixo/constants/Text'

// Premissas default do modelo (bps quando take rate / fee, fração quando alocação).
//
// ⚠️ **Espelho, não fonte.** Em runtime quem alimenta o engine é a store, com as
// premissas do ESCRITÓRIO vindas do backend (spec-017); a fonte única é
// `aawz_fee_fixo/core/premissas.py`. Este objeto é a referência de leitura e o
// que os testes semeiam — `test_paridade_premissas_frontend.py` quebra no dia em
// que um dos dois lados mudar sozinho. Mudou aqui? Mude lá, no mesmo PR.
//
// Recalibrado pela spec-021 [T107b] a partir do guia AAWZ "Quando o fee-based é
// mais barato" (v3, jul/2026) — o material citável mais novo do próprio time:
//
// | Campo       | Antes           | Agora            | Fonte no guia         |
// |-------------|-----------------|------------------|-----------------------|
// | custo_cheio | (não existia)   | fa 200 · ret 150 | Tabela 1, p.12        |
// |             |                 | fp 38 · etf 15   |                       |
// | frac_classe | (não existia)   | eq/fi 1,0        | Gráfico 7, p.15       |
// | cb.eq       | 65              | 0                | p.19 e Tabela 1, p.12 |
// | cb.fi       | 30              | 7                | Tabela 1, p.12        |
// | ni_fb       | 15              | 27               | p.18                  |
// | fee_prof    | 70              | 60               | p.28                  |
// | fee_plat    | 20              | 15               | p.28                  |
// | alc_f       | soma 1,05 (bug) | migração média   | Tabela 2b, p.13       |
// | giro_ini    | 1,0             | 0,24             | p.16 — ver abaixo     |
// | giro_fim    | 0,1             | 0,24             | p.16 — ver abaixo     |
// | frac        | 0,4             | 1,0 (DEPRECATED) | virou `frac_classe`   |
//
// `custo_cheio` é o que sai do BOLSO do cliente na classe; `tr` continua sendo o
// que a PLATAFORMA captura. A diferença é a fatia do gestor: o fundo ativo custa
// 200 cheios e a plataforma captura 47, então 153 ficam com quem gere o fundo.
// Sem essa camada o custo comissionado saía 117 (visão da plataforma) em vez de
// 164 (visão do cliente), e a economia da migração saía subestimada na mesma
// medida. A fatia do gestor é DERIVADA, nunca digitada.
//
// `null` na classe é AUSÊNCIA DECLARADA, não buraco: "esta classe não tem custo
// cheio próprio — o que sai do bolso é o take rate efetivo pelo giro". Vale para
// o Equity ativo e a renda fixa, que o guia trata como 100% transacionais (p.15),
// e para o float, que não tem taxa. Escrever `eq: 143` seria errado nos dois
// sentidos: no comissionado o cliente paga 143 só porque gira, e no maduro 34.
//
// Os dois giros têm o MESMO valor porque o `giro` do motor é a razão sobre o
// ritmo do comissionado (que é a referência, giro = 1), e a contenção acontece
// no dia 1: p.16 — "o giro é contido já na entrada e não depende de a alocação
// migrar, essa economia vale desde o primeiro ano". O guia dá a conta: o giro cai
// de ~50% para 12% do book ao ano e a receita transacional "retrai cerca de 76%"
// ⇒ 12/50 = 0,24, que confere com os pisos da Tabela 1 (143 × 0,24 = 34,3 ≈ 34
// no Equity; 68 × 0,24 = 16,3 ≈ 16 na renda fixa). Nada disso virou `if` no
// motor: a interpolação segue verbatim, então cenário salvo com `giro_ini` 1,0
// continua interpolando como sempre.
export const DEF = {
  tr: { eq: 143, etf: 15, fi: 68, fa: 47, fp: 25, ret: 48, fl: 0 },
  // Tabela 1 (p.12). `null` = ausência declarada (ver acima).
  custo_cheio: {
    eq: null,
    etf: 15,
    fi: null,
    fa: 200,
    fp: 38,
    ret: 150,
    fl: null,
  },
  // DEPRECATED (spec-021): sobrevive porque todo cenário salvo antes da
  // recalibração o carrega e o motor precisa lê-lo para reproduzir esses
  // cenários intactos. Vale 1,0 — o que `frac_classe.eq` diz —, e não 0,4, para
  // que o campo obsoleto não contradiga o novo enquanto existir.
  frac: 1,
  // Gráfico 7 (p.15). Fundos ativos e previdência têm parcela transacional no
  // take rate da PLATAFORMA, mas o custo no BOLSO deles é a taxa de
  // administração, que não muda com o giro (Tabela 3, p.17) — e é o bolso que
  // este mapa modula. Por isso 0 nas classes com `custo_cheio`.
  frac_classe: { eq: 1, etf: 0, fi: 1, fa: 0, fp: 0, ret: 0, fl: 0 },
  // ★ 12/08/2026, decisão do owner: a simulação modela INVESTIMENTOS e só eles.
  // Câmbio, seguros e cartões saíram dos dois lados da conta. O campo continua
  // existindo valendo zero para que cada cenário salvo se date sozinho — os
  // antigos trazem 42/27 no próprio `P` e reproduzem o que o cliente viu. Ver
  // `CAMPOS_FORA_DO_MODELO` em `core/premissas.py`, que é a fonte.
  ni_comis: 0,
  ni_fb: 0,
  fee_prof: 60,
  fee_plat: 15,
  cb: { eq: 0, etf: 0, fi: 7, fa: 45, fp: 8, ret: 50, fl: 0 },
  // ★ 12/08/2026 (owner): `fl` era 0,073 e a soma dava 1,001 — arredondamento da
  // Tabela 2b do guia, invisível até a folha de alocação do PDF passar a
  // imprimir a linha de total ("Total 100,1%"). Ajustado para fechar 100%.
  //
  // NÃO move um único número de custo, medido: o float tem `tr: 0`,
  // `custo_cheio: null` e `cb: 0`, então o peso dele não entra em conta nenhuma.
  alc_c: {
    eq: 0.252,
    etf: 0,
    fi: 0.345,
    fa: 0.256,
    fp: 0,
    ret: 0.075,
    fl: 0.072,
  },
  // Migração média da Tabela 2b (p.13) — soma 1,00. O default anterior somava
  // 1,05, o que inflava o custo fee em ~5% da conta por classe.
  alc_f: {
    eq: 0.02,
    etf: 0.3,
    fi: 0.17,
    fa: 0.03,
    fp: 0.27,
    ret: 0.13,
    fl: 0.08,
  },
  giro_ini: 0.24,
  giro_fim: 0.24,
  // A cadeia do repasse (spec-022 [T201]/[T202]) — quanto do custo do cliente
  // chega ao PROFISSIONAL, na ordem em que o dinheiro anda: a plataforma
  // repassa uma fração da captura ao escritório, o escritório apura imposto
  // sobre esse repasse e repassa uma fração do líquido ao profissional.
  //
  // Fonte: 2º áudio de feedback do owner (2026-07-31) — "acho que é 72 bipes, a
  // plataforma tá passando 28,5 para o escritório. Aí desses 28,5, ela, o
  // escritório apura 19,9% de imposto e repassa 45% para o profissional".
  //
  // ⚠️ `repasse_plataforma` é a DIVISÃO 28,5/72 — o dado citável é o par, não o
  // quociente. O backend a guarda escrita assim (`premissas.py`); aqui ela vem
  // como decimal porque o teste de paridade parseia este objeto como JSON e uma
  // expressão não parsearia. As 16 casas são o round-trip exato do mesmo double
  // (0,3958 truncado daria 28,4976 e moveria o golden na quinta casa).
  repasse_plataforma: 0.3958333333333333,
  imposto_repasse: 0.199,
  // O antigo knob `P.repasse` do laboratório, agora premissa do escritório: é a
  // mesma grandeza (fração do líquido que vai ao profissional) e o mesmo 0,45.
  repasse_profissional: 0.45,
}

// Ordem canônica das classes de ativo.
export const K = ['eq', 'fi', 'fa', 'ret', 'fl', 'etf', 'fp']

// Nome de cada classe na tela — o vocabulário do RELATÓRIO, que é onde o
// consultor lê os mesmos números (tabela de take rates do guia AAWZ v3,
// transcrita nos BRIEFs das specs 001 e 003: "Equity ativo 143, Equity ETF 15,
// Renda fixa 68, Fundos ativos 47, Fundos passivos 25, Previdência 48, Float").
//
// A regra que o relatório pratica é português, EXCETO onde o termo de mercado é
// o inglês — por isso `Equity`, `ETF` e `Float` ficam, e traduzi-los seria
// passar do ponto. O plural em "Fundos" é do relatório e diz algo: no singular
// a linha se lê como um fundo específico, e ela nomeia a classe inteira.
//
// As CHAVES não acompanham: elas são contrato (o cenário salvo as grava e o
// gate de paridade do backend as compara). `utils/nomes-de-classe.unit.js`
// prende os dois lados — os quatro nomes novos e os três que não mudaram.
export const KLABEL = {
  eq: 'Equity ativo',
  etf: 'Equity ETF',
  fi: 'Renda fixa',
  fa: 'Fundos ativos',
  fp: 'Fundos passivos',
  ret: 'Previdência',
  fl: 'Float',
}

// Presets de alocação-alvo do fee (fração por classe, soma ~1).
//
// **São as três colunas de migração do relatório**, e só elas: "Migração leve",
// "Migração média" e "100% ETF + passivos" da tabela de empilhamento do custo no
// ponto maduro (Tabela 2b, p.13 — a que o owner cita como Tabela 4). Cada uma
// está ancorada por golden em `utils/fee-lab-paridade-guia.unit.js`: o mapa é
// comparado classe a classe com a coluna, e o que ele produz é comparado com o
// número publicado (custo dos produtos 49 · 32 · 21; take final 109 · 92 · 81 no
// consultor e 124 · 107 · 96 no assessor).
//
// **O que saiu, e por quê** (medido em 03/08/2026, antes de apagar): os quatro
// presets anteriores vinham do protótipo (`efc0807ce`) como mapas fixos — nada
// os derivava do cenário do cliente — e a recalibração da spec-021 mudou as
// premissas por baixo deles. Os nomes passaram a mentir:
//
// | preset            | prometia            | entregava (contra 163,9 do comis.) |
// |-------------------|---------------------|------------------------------------|
// | "Custo-neutro"    | empatar             | 135,0 bps — 17,6% mais barato      |
// | "10% mais barato" | 10%                 | 127,1 bps — 22,5%                  |
// | "20% mais barato" | 20%                 | 121,6 bps — 25,9%                  |
//
// Não se perdeu ferramenta: o que eles ofereciam era um gradiente de alvos
// intermediários, e o gradiente do relatório (49 → 32 → 21 bps) é o mesmo
// serviço com números que alguém pode citar. Quem precisa de um alvo fora da
// tabela usa o "Editável", que é o modo de mexer à mão.
export const PRESETS = {
  leve: {
    eq: 0.15,
    etf: 0.08,
    fi: 0.345,
    fa: 0.18,
    fp: 0.08,
    ret: 0.09,
    fl: 0.075,
  },
  // A migração média é o MESMO mapa do `alc_f` default. A tela abre neste preset
  // (`defaultLabState`), então dois valores diferentes fariam o simulador abrir
  // num alvo que a premissa do escritório não descreve, e a tela contradiria a
  // aba de configurações.
  //
  // ⚠️ **A chave continua `fb`, e não `media`, de propósito.** Ela é gravada em
  // todo cenário salvo e tem dois leitores que a comparam por igualdade:
  // `utils/alocacao-origem.js` (e o espelho `rotular_alocacao` do backend), que
  // a lê como "ninguém escolheu alocação nenhuma", e o fixture
  // `apoio_abertura_de_custos.py`. Renomear faria toda análise já entregue
  // passar a se anunciar como "alocação informada pelo consultor" — que é
  // exatamente o G-2 do QA da spec-021, corrigido custe o que custar. O nome de
  // TELA está em `PRESET_NAME`; é ele que o consultor lê.
  fb: {
    eq: 0.02,
    etf: 0.3,
    fi: 0.17,
    fa: 0.03,
    fp: 0.27,
    ret: 0.13,
    fl: 0.08,
  },
  etf_passivos: {
    eq: 0,
    etf: 0.6,
    fi: 0,
    fa: 0,
    fp: 0.4,
    ret: 0,
    fl: 0,
  },
}

/**
 * O preset com que o laboratório ABRE (`defaultLabState`).
 *
 * Vive aqui, e não inline no engine, porque tem DOIS leitores: o estado inicial
 * do lab e o rotulador de origem da alocação (`utils/alocacao-origem.js`), que
 * precisa saber qual chave significa "ninguém escolheu nada". Enquanto o
 * segundo não conhecia esta constante, ele lia o alvo inicial como escolha do
 * consultor e toda simulação recém-aberta anunciava uma alocação que o
 * consultor não informou (G-2 do QA da spec-021).
 */
export const ALLOC_KEY_INICIAL = 'fb'

/**
 * O que o seletor OFERECE, na ordem em que aparece — o vocabulário do relatório.
 *
 * A ordem é a do gradiente de migração (mais leve → mais agressiva), que é a
 * ordem em que a tabela publica as colunas e a que faz a linha de custo cair da
 * esquerda para a direita. "Editável" fecha a lista porque é o único que não é
 * um cenário do relatório, e sim o modo de o consultor mexer à mão.
 */
export const PRESET_NAME = {
  leve: 'Migração leve',
  fb: 'Migração média',
  etf_passivos: '100% ETF + passivos',
  custom: 'Editável',
}

/**
 * As alocações dos presets aposentados, para os cenários que já as usam.
 *
 * Não são oferecidas a ninguém (por isso estão fora de `PRESETS`, que o teste de
 * paridade mantém fechado nas três colunas do relatório) — existem para que o
 * cenário salvo continue sendo REVERSÍVEL: quem abre uma análise antiga, clica
 * em "Migração média" para comparar e volta ao preset original tem de receber a
 * alocação original de volta. Sem elas, `setAlloc` trocaria só o rótulo e a tela
 * passaria a exibir "Custo-neutro" sobre os pesos da migração média.
 */
export const PRESETS_LEGADO = {
  neutro: {
    eq: 0.18,
    etf: 0.08,
    fi: 0.34,
    fa: 0.18,
    fp: 0.07,
    ret: 0.075,
    fl: 0.075,
  },
  m10: {
    eq: 0.13,
    etf: 0.15,
    fi: 0.3,
    fa: 0.12,
    fp: 0.15,
    ret: 0.075,
    fl: 0.075,
  },
  m20: {
    eq: 0.08,
    etf: 0.2,
    fi: 0.28,
    fa: 0.08,
    fp: 0.21,
    ret: 0.075,
    fl: 0.075,
  },
}

/**
 * A alocação de uma chave de preset — a única porta, incluindo o legado.
 *
 * @param {string} chave `allocKey`.
 * @return {Object|null} mapa por classe, ou `null` se a chave não for preset
 *   (o `custom`, por exemplo, cuja alocação é a que o usuário editou).
 */
export const alocacaoDoPreset = (chave) =>
  PRESETS[chave] || PRESETS_LEGADO[chave] || null

/**
 * Rótulos de presets que saíram do seletor mas sobrevivem em cenários SALVOS.
 *
 * Cenário salvo é imutável: ele guarda o `alloc` inteiro, então os números
 * reproduzem intactos — o que se perderia sem este mapa é só o nome, e uma
 * análise reaberta mostraria um rótulo vazio onde havia "Custo-neutro". Ficam
 * fora de `PRESET_NAME` para não voltarem a ser escolhíveis: são alvos sem
 * coluna no relatório e sem golden (ver o comentário de `PRESETS`).
 */
export const PRESET_NAME_LEGADO = {
  neutro: 'Custo-neutro',
  m10: '10% mais barato',
  m20: '20% mais barato',
}

/**
 * O nome de tela de uma chave de alocação — a única porta para rotular preset.
 *
 * Existe porque são três os consumidores (o título do lado "Depois" da estrutura
 * da carteira, o resumo da simulação em lote e a coluna da tabela de migração) e
 * cada um lendo `PRESET_NAME[chave]` direto significaria três lugares para
 * lembrar do legado. Chave desconhecida volta como ela mesma, nunca `undefined`:
 * um rótulo estranho é lido e reportado; um rótulo vazio passa despercebido.
 *
 * @param {string} chave `allocKey` do cenário.
 * @return {string}
 */
export const nomeDoPreset = (chave) =>
  PRESET_NAME[chave] || PRESET_NAME_LEGADO[chave] || String(chave || '')

/**
 * As options do seletor de alocação, garantindo que a chave ATUAL esteja entre
 * elas.
 *
 * A garantia não é cosmética. O toggle da estrutura da carteira é um
 * `v-btn-toggle` com `mandatory`: quando nenhuma option bate com o `value`, o
 * Vuetify escolhe a primeira **e emite `change`** — na prática reescrevendo a
 * alocação do cenário aberto. Foi o que acontecia com o `custom` antes de ele
 * entrar na lista, e voltaria a acontecer ao reabrir uma análise salva com um
 * dos presets aposentados. Aqui o alvo legado aparece como pill só para o
 * cenário que já o usa, no fim da lista; ninguém mais o vê.
 *
 * @param {string} chaveAtual `allocKey` do cenário.
 * @return {Array<{text: string, value: string}>}
 */
export const opcoesDePreset = (chaveAtual) => {
  const opcoes = Object.keys(PRESET_NAME).map((key) => ({
    text: PRESET_NAME[key],
    value: key,
  }))
  const conhecida = opcoes.some((opcao) => opcao.value === chaveAtual)
  if (chaveAtual && !conhecida) {
    opcoes.push({ text: nomeDoPreset(chaveAtual), value: chaveAtual })
  }
  return opcoes
}

// Cores dos 3 regimes — derivadas dos tokens do design-system (STATUS_COLORS).
export const REGIME_COLORS = {
  comis: STATUS_COLORS.em_potencial, // amber #F39C12
  fee: STATUS_COLORS.elegivel, // primary green #56AF79
  cons: STATUS_COLORS.migracao_parcial, // blue #3498DB
}

// Cores dos 3 regimes pro gráfico "Projeção ano a ano" — divergem de
// REGIME_COLORS (chips dos cards de regime, não mexer): comissionado vira
// neutro-escuro (era âmbar), fee vira âmbar (era verde), consultor vira
// verde (era azul). Canvas não lê CSS var — hex literal, mesmo racional de
// REGIME_COLORS.
export const REGIME_CHART_COLORS = {
  comis: '#27272A', // neutral-900 (design/_controls.sass) — tracejado
  fee: STATUS_COLORS.em_potencial, // amber #F39C12
  cons: STATUS_COLORS.elegivel, // green #56AF79
}

// Paleta por classe de ativo — derivada da paleta serial de charts do DS.
export const KCOLOR = {
  eq: '#1A2E66', // azul profundo
  etf: '#245CA6', // azul royal
  fi: '#006D77', // petróleo
  fa: '#6C4F9B', // roxo
  fp: '#2E638D', // azul navy
  ret: '#F5A623', // laranja
  fl: '#A8A8A8', // cinza premium
}

// Tolerância de soma dos pesos (fração, G1 do card de composição). 0,5 pp —
// calibrada porque o preset comissionado padrão (P.alc_c) soma 1,001 (100,1%);
// tolerância zero faria o chip G1 aparecer sempre no lado comissionado.
export const TOLERANCIA_SOMA_PESOS = 0.005

// Limiar de contribuição negativa agregada (bps, G2 do card de composição).
// 0,5 bps — calibrado porque, com premissas default, Retirement já rende
// trLiq = -2 bps e contribui -0,15 bps; sem limiar o chip apareceria sempre.
export const LIMIAR_CREDITO_BPS = 0.5

// Limiar abaixo do qual o efeito-mix é tratado como "praticamente neutro"
// (bps, copy alternativa de rodapé do card de composição).
export const LIMIAR_MIX_NEUTRO_BPS = 0.1

// Textos explicativos dos controles (tooltips). Fonte: protótipo validado.
export const TOOLTIPS = {
  composicao:
    'Aplica a mesma taxa líquida por classe (take rate menos cashback, já nas regras fee) aos pesos da carteira atual e da carteira-alvo, isolando o efeito da realocação. Não inclui receita não-investimentos, fee do profissional, giro do modelo comissionado nem a parcela de cashback retida pela assessoria. Para o custo total do cliente, veja o gráfico "Custo do cliente ao longo do tempo".',
  auc: 'Volume sob custódia do cliente. Escala tudo em reais — custo e receita são bps × AuC.',
  horizonte:
    'Anos projetados. O fee começa mais caro e barateia conforme a carteira migra.',
  migracao:
    'Em quantos anos a carteira sai da alocação comissionada e chega ao alvo fee. d+0 = troca tudo no dia 1; Gradual (5 anos) cruza o comissionado por volta do ano 3–4.',
  // As classes são citadas com o nome que a grade e o relatório usam (`KLABEL`).
  // "FI" não era nome de nada na tela, e este tooltip abre exatamente ao lado
  // da linha que hoje se chama "Renda fixa".
  alocacao:
    'Define a carteira-alvo do fee. Trocar classes caras (Equity ativo, Fundos ativos, Renda fixa) por baratas (Equity ETF, Fundos passivos, Previdência) derruba o TR e o custo do cliente. TR líq = custo líquido da classe.',
  rentabilidade:
    'Retorno bruto esperado, antes de custos. Afeta só a projeção de patrimônio; não altera os bps de custo.',
  alfa: 'Ganho líquido extra do acompanhamento fiduciário (Vanguard Advisor’s Alpha). Soma ao retorno só nos regimes fee e consultor.',
  // spec-022 [US-4]: a 2ª frase dizia "e é a receita do assessor nos regimes
  // fee/consultor", o que deixou de ser verdade na W2 — a receita passou a ser
  // `fee_prof × (1 − imposto) × repasse_profissional` (60 → 21,627 bps). O card
  // "Receita do assessor" exibia 21,6 enquanto este tooltip afirmava 60: duas
  // leituras divergentes na mesma tela.
  // Owner 2026-08-03: a 2ª frase valia para os dois canais do fee. Não vale —
  // na consultoria CVM o profissional cobra direto do cliente e fica com o fee
  // cheio; só na assessoria o dinheiro passa pelo escritório antes.
  fee_prof:
    'Fee fixo do assessor/consultor sobre AuC (padrão AAWZ 0,6%). É o que o CLIENTE paga ao profissional — tanto na assessoria quanto na consultoria CVM ele entra no escritório, e o que chega a quem atende sai da cadeia do repasse (imposto e divisão), sempre menor que o fee cobrado.',
  // spec-022: este é o ÚLTIMO degrau da cadeia, não a fração cheia da captura.
  // O texto anterior dizia "fração do take rate que chega ao assessor", que era
  // verdade quando o knob era aplicado direto sobre a captura — com a cadeia,
  // antes dele vêm o repasse da plataforma e o imposto do escritório.
  //
  // Owner 2026-08-03 ("repasse do assessor não deveria impactar a simulação no
  // consultor CVM"): o slider move comissionado e fee-assessoria, e NÃO a
  // consultoria. Sem esta frase o card do consultor ficaria parado enquanto o
  // slider anda, e o leitor concluiria que a tela travou.
  repasse:
    'Do que sobra no escritório depois do imposto, quanto vai para o profissional. É o último degrau da cadeia do repasse, e vale nos três regimes — antes dele vêm o repasse da plataforma (só no comissionado) e o imposto sobre o que entra no escritório.',
  repasse_plataforma:
    'Da receita de investimentos que a plataforma captura, quanto ela repassa ao escritório. Referência AAWZ: 28,5 de cada 72 bps capturados.',
  imposto_repasse:
    'O que o escritório apura de imposto sobre o que recebe — o repasse da plataforma no comissionado, o fee cobrado nos dois canais fee-based. Incide antes da divisão com o profissional.',
  // spec-022 [US-4]: o erro central do áudio 1 ("tu considerou o fee da
  // plataforma como fee do profissional") sobrevivia neste tooltip, e a US-4 o
  // corrigiu escrevendo que a taxa vinha POR CIMA e não saía do fee.
  //
  // ★ 11/08/2026 — o owner inverteu o modelo, e o texto teve de virar junto:
  // existe UM fee, e é de dentro dele que a plataforma retém. Os dois motores
  // acompanham (`custo_fee_assessor_bps == custo_fee_bps` aqui e em
  // `relatorio_projecao.py`).
  //
  // O que a [US-4] travou CONTINUA valendo, e é o que este texto não pode
  // perder: a taxa é da plataforma, não de quem atende. Dizer "retém" sem
  // dizer de quem ela é reintroduziria a confusão original pelo outro lado —
  // por isso as duas coisas estão na mesma frase.
  fee_plat:
    'O que a plataforma AAWZ retém de dentro do fee do profissional, no canal assessoria — é receita da plataforma, não de quem atende você, e o custo do cliente não muda por causa dela. Consultoria CVM = 0.',
  cb_dev:
    'Quanto do rebate a assessoria devolve ao cliente. Consultoria CVM devolve 100% por dever fiduciário.',
  // Fora do modelo desde 12/08/2026 — o painel não os oferece mais. Os textos
  // sobrevivem porque cenário salvo antes da mudança ainda exibe as premissas
  // dele no histórico, e ali o número 42/27 precisa continuar explicado.
  ni_comis:
    'Receita não-investimentos por ano no modelo comissionado (bps sobre AuC). Fora do modelo desde 12/08/2026: a simulação passou a considerar só investimentos.',
  ni_fb:
    'Receita não-investimentos por ano já no modelo fee maduro (bps sobre AuC). Fora do modelo desde 12/08/2026: a simulação passou a considerar só investimentos.',
  giro_ini:
    'Giro no início da migração, como razão do ritmo comissionado (1 = o mesmo giro de hoje). O padrão 0,24 é a contenção que o guia mede: de ~50% para 12% do book ao ano.',
  giro_fim:
    'Giro no fim da migração, já estabilizado. Igual ao inicial no padrão AAWZ: a contenção vale desde o ano 1, porque o incentivo a girar some junto com o rebate.',
  // Sem o número da spec: este texto é TOOLTIP, e "spec-021" não diz nada a
  // quem está com o cliente na frente. A rastreabilidade fica no comentário.
  frac: 'Obsoleta — substituída por "Fração transacional por classe". Só é lida em cenários salvos antes dessa mudança.',
  frac_classe:
    'Parcela do take rate da classe que depende de transação. 100% no Equity ativo e na renda fixa (o guia as trata como transacionais); 0 nas classes de estoque, cujo custo é a taxa de administração e não muda com o giro.',
  tr: 'Take rate bruto da classe (bps sobre AuC) antes do cashback — o que a PLATAFORMA captura.',
  custo_cheio:
    'O que sai do BOLSO do cliente na classe (bps sobre AuC), antes do cashback. A diferença para o take rate é a fatia retida pelo gestor: fundo ativo custa 200 cheios e a plataforma captura 47. Classe transacional não tem custo cheio próprio — o bolso paga o take rate efetivo pelo giro.',
  // "Rebate" é o que a plataforma recebe de volta da GESTORA (o take rate da
  // classe); o que chega ao CLIENTE é cashback. Este tooltip chamava os dois
  // pelo mesmo nome, e a tela ficava com uma palavra para duas grandezas.
  cb: 'Cashback devolvido ao cliente por classe (bps sobre AuC).',
  idadeAtual:
    'Idade atual do cliente — início da curva de projeção patrimonial no gráfico "Evolução do patrimônio · ciclo de vida".',
  idadeAposentadoria:
    'Idade em que a acumulação termina e começam as retiradas anuais.',
  longevidade: 'Idade até a qual a projeção de patrimônio é simulada.',
  retiradaAnual:
    'Valor retirado do patrimônio a cada ano, a partir da aposentadoria.',
  aporteAnual:
    'Valor investido a mais todo ano, além do patrimônio inicial, até a aposentadoria.',
}

// Estilo do wrapper <div> dos gráficos (vue-chartjs) — a altura precisa ir
// aqui, senão o Chart.js responsivo usa o default 400px e o gráfico vaza.
export const CHART_STYLES = { position: 'relative', height: '260px' }

// Limites do slider/input de AuC (patrimônio). Slider step de R$ 5M para
// granularidade razoável; edição manual permite até R$ 500M.
export const AUC_SLIDER_MIN = 0
export const AUC_SLIDER_MAX = 500000000
export const AUC_SLIDER_STEP = 5000000
export const AUC_EDIT_MIN = 0
export const AUC_EDIT_MAX = 500000000

// Faixas dos controles que o assistente pode sugerir ajustar (spec-014, US-3).
//
// Vivem aqui, e não dentro do painel, porque desde a spec-014 existem DOIS
// leitores: o controle que o consultor arrasta e o validador que recusa a
// sugestão fora da faixa. Duas listas divergiriam na primeira mudança de faixa,
// e o assistente passaria a propor um valor que o controle não aceita.
export const HORIZONTE_OPTIONS = [
  { text: '1 ano', value: 1 },
  { text: '3 anos', value: 3 },
  { text: '5 anos', value: 5 },
]

export const MIGRACAO_OPTIONS = [
  { text: 'd+0', value: 1 },
  { text: '3 anos', value: 3 },
  { text: '5 anos', value: 5 },
]

// 0 a 200 bps desde 03/08/2026: os 30-120 anteriores recusavam arranjos reais
// nas duas pontas — fee promocional abaixo de 0,3% e o 1,5% que private e
// consultoria praticam. A grade de múltiplos de 5 não muda (30 era múltiplo de
// 5), então nenhuma sugestão antes aceita passa a ser recusada.
export const FEE_PROF_FAIXA = { min: 0, max: 200, step: 5 }
